/**
 * Portal design tokens — single source of truth for light/dark palettes.
 * Use `createAppTheme(mode)` in theme.js and `theme.portal` in styled components.
 * Static exports below default to light mode for backward compatibility.
 */

export const PORTAL_SIDEBAR_WIDTH = 236;
export const PORTAL_FONT_FAMILY = "'DM Sans', 'Poppins', Arial, sans-serif";

export const DEFAULT_PORTAL_MODE = 'light';

/** Semantic tokens per color mode. Add keys here when introducing new surfaces. */
export const PORTAL_THEME_MODES = {
  light: {
    accent: '#E87272',
    accentHover: '#D45F5F',
    accentSoft: '#FEF0EE',
    accentMuted: '#E8AFA8',
    pageBg: '#F5F4F2',
    surface: '#ffffff',
    surfaceMuted: '#F7F5F3',
    sidebarBg: '#ffffff',
    text: '#1A1614',
    textMuted: '#6B6560',
    textSubtle: '#9B9590',
    textFaint: '#B0AAA6',
    border: '#EFEDEA',
    inputBorder: '#E5E1DD',
    cardShadow: '0 1px 3px rgba(0,0,0,.04)',
    tableHeaderBg: '#F5F4F2',
    tableRowHover: '#F7F5F3',
  },
  dark: {
    accent: '#E87272',
    accentHover: '#F08888',
    accentSoft: 'rgba(232, 114, 114, 0.16)',
    accentMuted: '#C48A84',
    pageBg: '#121110',
    surface: '#1C1917',
    surfaceMuted: '#252220',
    sidebarBg: '#1C1917',
    text: '#F5F4F2',
    textMuted: '#A8A29E',
    textSubtle: '#78716C',
    textFaint: '#57534E',
    border: '#2E2A27',
    inputBorder: '#3D3834',
    cardShadow: '0 1px 3px rgba(0,0,0,.32)',
    tableHeaderBg: '#1C1917',
    tableRowHover: '#252220',
  },
};

/** @param {'light' | 'dark'} [mode] */
export function getPortalTokens(mode = DEFAULT_PORTAL_MODE) {
  return PORTAL_THEME_MODES[mode] ?? PORTAL_THEME_MODES.light;
}

const light = getPortalTokens('light');

/** @deprecated Prefer theme.palette.primary.main or theme.portal.accent */
export const PORTAL_ACCENT = light.accent;
/** @deprecated Prefer theme.portal.accentHover */
export const PORTAL_ACCENT_HOVER = light.accentHover;
/** @deprecated Prefer theme.portal.accentSoft */
export const PORTAL_ACCENT_SOFT = light.accentSoft;
/** @deprecated Prefer theme.portal.accentMuted */
export const PORTAL_ACCENT_MUTED = light.accentMuted;
/** @deprecated Prefer theme.palette.background.default or theme.portal.pageBg */
export const PORTAL_PAGE_BG = light.pageBg;
/** @deprecated Prefer theme.palette.text.primary or theme.portal.text */
export const PORTAL_TEXT = light.text;
/** @deprecated Prefer theme.palette.text.secondary or theme.portal.textMuted */
export const PORTAL_TEXT_MUTED = light.textMuted;
/** @deprecated Prefer theme.portal.textSubtle */
export const PORTAL_TEXT_SUBTLE = light.textSubtle;
/** @deprecated Prefer theme.portal.textFaint */
export const PORTAL_TEXT_FAINT = light.textFaint;
/** @deprecated Prefer theme.palette.divider or theme.portal.border */
export const PORTAL_BORDER = light.border;
/** @deprecated Prefer theme.portal.inputBorder */
export const PORTAL_INPUT_BORDER = light.inputBorder;
/** @deprecated Prefer theme.portal.cardShadow */
export const PORTAL_CARD_SHADOW = light.cardShadow;
/** @deprecated Prefer theme.portal.surface */
export const PORTAL_SURFACE = light.surface;
/** @deprecated Prefer theme.portal.surfaceMuted */
export const PORTAL_SURFACE_MUTED = light.surfaceMuted;
/** @deprecated Prefer theme.portal.sidebarBg */
export const PORTAL_SIDEBAR_BG = light.sidebarBg;
/** @deprecated Prefer theme.portal.tableHeaderBg */
export const PORTAL_TABLE_HEADER_BG = light.tableHeaderBg;
/** @deprecated Prefer theme.portal.tableRowHover */
export const PORTAL_TABLE_ROW_HOVER = light.tableRowHover;

/** Legacy alias used across events/partner modules. */
export const BRAND_ORANGE = PORTAL_ACCENT;
